import React from "react";

import { useCurrentUserQuery } from "@features/investments/queries/current-user";
import { useInvestmentsAccessQuery } from "@features/investments/queries/access";

type InvestmentsAccountMenuProps = {
  modeLabel?: string;
};

export function InvestmentsAccountMenu({ modeLabel }: InvestmentsAccountMenuProps): JSX.Element {
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = React.useState(false);
  const [imageFailed, setImageFailed] = React.useState(false);
  const currentUserQuery = useCurrentUserQuery();
  const accessQuery = useInvestmentsAccessQuery();

  const currentUser = currentUserQuery.data?.authenticated ? currentUserQuery.data.user : undefined;
  const fallbackUser = accessQuery.data?.user;
  const canSeeWatchlist = accessQuery.data?.isGod === true;
  const displayName = currentUser?.name || fallbackUser?.name || "User";
  const displayEmail = currentUser?.email || fallbackUser?.email || "";
  const displayPicture = currentUser?.picture || "";
  const showImage = displayPicture !== "" && !imageFailed;
  const resolvedModeLabel = modeLabel ?? (canSeeWatchlist ? "Operator Mode" : "Snapshot Research");

  React.useEffect(() => {
    setImageFailed(false);
  }, [displayPicture]);

  React.useEffect(() => {
    if (!open) {
      return undefined;
    }
    function handlePointerDown(event: MouseEvent): void {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    function handleKeyDown(event: KeyboardEvent): void {
      if (event.key === "Escape") {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  async function handleLogout(): Promise<void> {
    setOpen(false);
    try {
      await fetch("/auth/logout", {
        method: "POST",
        credentials: "include",
      });
    } finally {
      window.location.href = "/#/";
    }
  }

  function handleOpenWatchlist(): void {
    setOpen(false);
    window.location.hash = "#/investments/portfolio";
  }

  function handleOpenOperator(): void {
    setOpen(false);
    window.location.hash = "#/investments/operator";
  }

  function handleOpenPrivateNews(): void {
    setOpen(false);
    window.location.hash = "#/swing-trading/news";
  }

  function handleOpenGlossary(): void {
    setOpen(false);
    window.location.hash = "#/investments/reference/glossary";
  }

  function handleOpenHelp(): void {
    setOpen(false);
    window.location.hash = "#/investments/reference/help";
  }

  return (
    <div ref={containerRef} className="investments-account">
      <button
        type="button"
        className="investments-account-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        {showImage ? (
          <img
            className="investments-account-avatar-image"
            src={displayPicture}
            alt={displayName}
            referrerPolicy="no-referrer"
            onError={() => setImageFailed(true)}
          />
        ) : (
          <span className="investments-account-avatar">{buildInitials(displayName)}</span>
        )}
        <span className="investments-account-name">{displayName}</span>
        <span className={`investments-account-caret${open ? " open" : ""}`} aria-hidden="true">▾</span>
      </button>

      {open && (
        <div className="investments-account-menu" role="menu" aria-label="Account menu">
          <div className="investments-account-menu-header">
            <div className="investments-account-menu-name">{displayName}</div>
            {displayEmail && <div className="investments-account-menu-email">{displayEmail}</div>}
            <div className="investments-account-menu-header-divider" role="separator" aria-hidden="true" />
            <div className="investments-account-menu-mode">
              {resolvedModeLabel}
            </div>
          </div>
          {canSeeWatchlist && (
            <button type="button" className="investments-account-menu-item" onClick={handleOpenOperator}>
              Console
            </button>
          )}
          {canSeeWatchlist && (
            <button type="button" className="investments-account-menu-item" onClick={handleOpenWatchlist}>
              Portfolio
            </button>
          )}
          {canSeeWatchlist && (
            <button type="button" className="investments-account-menu-item" onClick={handleOpenPrivateNews}>
              Private News
            </button>
          )}
          <button type="button" className="investments-account-menu-item" onClick={handleOpenHelp}>
            Help / FAQ
          </button>
          <button type="button" className="investments-account-menu-item" onClick={handleOpenGlossary}>
            Glossary
          </button>
          {canSeeWatchlist && <>
            <div className="investments-account-menu-separator" role="separator" aria-hidden="true" />
            <button type="button" className="investments-account-menu-item" onClick={() => { setOpen(false); window.location.hash = "#/swing-trading"; }}>
              Swing Trading
            </button>
            <button type="button" className="investments-account-menu-item" onClick={() => { setOpen(false); window.location.hash = "#/trading-checklist"; }}>
              Trading Checklist
            </button>
          </>}
          <div className="investments-account-menu-separator" role="separator" aria-hidden="true" />
          <button type="button" className="investments-account-menu-item" onClick={() => void handleLogout()}>
            Logout
          </button>
        </div>
      )}
    </div>
  );
}

function buildInitials(name: string): string {
  const parts = name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2);
  if (parts.length === 0) {
    return "U";
  }
  return parts.map((part) => part[0]?.toUpperCase() || "").join("");
}
